import { ActionsObservable, ofType, StateObservable } from 'redux-observable';
import { forkJoin, Observable, of } from 'rxjs';
import {
    catchError,
    concatMap,
    filter,
    map,
    switchMap,
    takeUntil,
} from 'rxjs/operators';

import {
    DEFAULT_RECENTLY_ACTIVE,
    DEFAULT_RECENTLY_ACTIVE_DAYS,
    DEFAULT_RECENTLY_CLOSED,
    DEFAULT_RECENTLY_CLOSED_DAYS,
    DEFAULT_TEAM_FOCUSES,
    DEFAULT_TEAM_RECENTLY_ACTIVE_DAYS,
    DEFAULT_TEAM_RECENTLY_CLOSED_DAYS,
    FocusSelectionParams,
} from '../../coaching-base/config';
import { CommitmentKpi } from '../../coaching-base/general';
import { TeamOAService } from '../../coaching-insight/oa/services/team-oa.service';
import { MetricItem } from '../../coaching-tags/models/metric.coaching.model';
import { MetricModelSetTransformer } from '../../insight/performance-data/metric.pd.model';
import { Dependencies } from '../../store';
import { PayloadAction } from '../../store.model';
import { PeriodType } from '../../widget/date-range.widget.model';
import {
    injectUnsavedLinkedObservations,
    transformGoalEventsHistoryResponse,
    transformGoalHistoryResponse,
} from '../coaching-commitment.model';
import { FocusKPI } from '../models/commitment.model';
import { CommitmentService } from '../services/commitment.service';

import {
    ActionType,
    GoalCommitmentInitAction,
    goalCommitmentInitResult,
    GoalCommitmentRetrieveDataAction,
    goalCommitmentRetrieveDataResult,
    GoalCommitmentRetrieveGoalEventHistoryAction,
    goalCommitmentRetrieveGoalEventHistoryResult,
    GoalCommitmentRetrieveGoalHistoryAction,
    goalCommitmentRetrieveGoalHistoryResult,
    LoadGoals,
    loadGoalsResult,
} from './goals.commitment.action';
import { GoalFormValue } from './goals.commitment.form';

export interface ChainGroup<TPayload> {
    root: TPayload;

    // lookups
    metrics?: MetricItem[];

    performerId?: number;
    commitmentId?: number;
    behaviourId?: number;
    subBehaviourId?: number;
    observationIds?: number[];

    // currently selected goal that's loaded in the form
    selected?: GoalFormValue;

    commitmentKpis?: CommitmentKpi[];
    focusKpis?: FocusKPI[];
    goalsFromOtherCommitments?: CommitmentKpi[];
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const goalCommitmentInit = (
    action: ActionsObservable<
        PayloadAction<ActionType, GoalCommitmentInitAction>
    >,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__INIT),
        switchMap(
            (act: PayloadAction<ActionType, GoalCommitmentInitAction>) => {
                const service =
                    deps.injector.get<CommitmentService>(CommitmentService);

                return of(null).pipe(
                    service.listFocuses(() => {
                        const params: FocusSelectionParams =
                            act.payload.focusSelectionParams ?? {};
                        return {
                            userId: act.payload.performerId,
                            recentlyActive:
                                params.recentlyActive != null
                                    ? params.recentlyActive
                                    : DEFAULT_RECENTLY_ACTIVE,
                            recentlyActiveDays:
                                params.recentlyActiveDays != null
                                    ? params.recentlyActiveDays
                                    : DEFAULT_RECENTLY_ACTIVE_DAYS,
                            recentlyClosed:
                                params.recentlyClosed != null
                                    ? params.recentlyClosed
                                    : DEFAULT_RECENTLY_CLOSED,
                            recentlyClosedDays:
                                params.recentlyClosedDays != null
                                    ? params.recentlyClosedDays
                                    : DEFAULT_RECENTLY_CLOSED_DAYS,
                            teamFocuses:
                                params.teamFocuses != null
                                    ? params.teamFocuses
                                    : DEFAULT_TEAM_FOCUSES,
                            teamRecentlyActiveDays:
                                params.teamRecentlyActiveDays != null
                                    ? params.teamRecentlyActiveDays
                                    : DEFAULT_TEAM_RECENTLY_ACTIVE_DAYS,
                            teamRecentlyClosedDays:
                                params.teamRecentlyClosedDays != null
                                    ? params.teamRecentlyClosedDays
                                    : DEFAULT_TEAM_RECENTLY_CLOSED_DAYS,
                        };
                    }),
                    map((ret) =>
                        goalCommitmentInitResult(
                            {
                                ...act.payload,
                                focusSummarySet: ret,
                                metrics: ret.coachableMetrics,
                            },
                            act.storePath,
                        ),
                    ),
                    catchError((err) => {
                        console.log(err);
                        return of(
                            goalCommitmentInitResult(
                                { ...act.payload },
                                act.storePath,
                            ),
                        );
                    }),
                );
            },
        ),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const goalCommitmentRetrieveData = (
    action: ActionsObservable<
        PayloadAction<ActionType, GoalCommitmentRetrieveDataAction>
    >,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_DATA),
        switchMap((act) => {
            const service = deps.injector.get<TeamOAService>(TeamOAService);

            return of(null).pipe(
                concatMap(() =>
                    act.payload.opts &&
                    act.payload.opts.periodType === PeriodType.ROLLING_30
                        ? service.retrieveCoachingPerformanceData(
                              act.payload.graphType,
                              act.payload.opts,
                          )
                        : service.retrievePerformanceData(
                              act.payload.graphType,
                              act.payload.opts,
                          ),
                ),
                map((ret) =>
                    new MetricModelSetTransformer(
                        act.payload.opts.displayNameCriteriaIdType,
                        [act.payload.opts.outputCriteriaIdType],
                    ).transform(ret),
                ),
                map((ret) =>
                    goalCommitmentRetrieveDataResult(
                        {
                            graphType: act.payload.graphType,
                            opts: act.payload.opts,
                            metricData: ret && ret.length !== 0 ? ret[0] : null, // act.payload.opts.metricId is expected to have one metric
                        },
                        act.storePath,
                    ),
                ),
                catchError((err) => {
                    console.log(err);
                    return of(
                        goalCommitmentRetrieveDataResult(
                            { ...act.payload },
                            act.storePath,
                        ),
                    );
                }),
                takeUntil(
                    action.pipe(
                        ofType(
                            ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_DATA_CANCEL,
                        ),
                        filter(
                            (cancelAct) =>
                                cancelAct.storePath === act.storePath,
                        ),
                    ),
                ),
            );
        }),
    );

// FOR THE GOALS
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const goalCommitmentRetrieveGoalHistory = (
    action: ActionsObservable<
        PayloadAction<ActionType, GoalCommitmentRetrieveGoalHistoryAction>
    >,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(
            ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_GOAL_HISTORY,
        ),
        switchMap((act) => {
            const service =
                deps.injector.get<CommitmentService>(CommitmentService);

            return of(null).pipe(
                concatMap(() =>
                    service.getGoalsHistory(
                        act.payload.commitmentId,
                        act.payload.performerId,
                        act.payload.goalId,
                    ),
                ),
                map((historyAndDataPoints) =>
                    transformGoalHistoryResponse(historyAndDataPoints),
                ),
                map((ret) =>
                    goalCommitmentRetrieveGoalHistoryResult(
                        {
                            commitmentId: act.payload.commitmentId,
                            performerId: act.payload.performerId,
                            goalId: act.payload.goalId,
                            goalHistory: ret.history,
                            goalDataPoints: ret.datapoints,
                        },
                        act.storePath,
                    ),
                ),
                catchError((err) => {
                    console.log(err);
                    return of(
                        goalCommitmentRetrieveGoalHistoryResult(
                            { ...act.payload },
                            act.storePath,
                        ),
                    );
                }),
                // takeUntil(action.pipe(
                //     ofType(ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_DATA_CANCEL),
                //     filter(cancelAct => cancelAct.storePath === act.storePath)
                // ))
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const goalCommitmentRetrieveGoalEventHistory = (
    action: ActionsObservable<
        PayloadAction<ActionType, GoalCommitmentRetrieveGoalEventHistoryAction>
    >,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(
            ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_GOAL_EVENT_HISTORY,
        ),
        switchMap((act) => {
            const service =
                deps.injector.get<CommitmentService>(CommitmentService);

            return of(null).pipe(
                concatMap(() =>
                    service.getGoalEventsHistory(
                        act.payload.commitmentId,
                        act.payload.performerId,
                        act.payload.goalId,
                    ),
                ),
                map((events) => transformGoalEventsHistoryResponse(events)),
                map((events) =>
                    injectUnsavedLinkedObservations(
                        events,
                        act.payload.linkedObservations,
                    ),
                ),
                map((ret) =>
                    goalCommitmentRetrieveGoalEventHistoryResult(
                        {
                            ...act.payload,
                            goalEventDataPoints: ret,
                        },
                        act.storePath,
                    ),
                ),
                catchError((err) => {
                    console.log(err);
                    return of(
                        goalCommitmentRetrieveGoalEventHistoryResult(
                            { ...act.payload },
                            act.storePath,
                        ),
                    );
                }),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const loadGoals = (
    action: Observable<PayloadAction<ActionType, LoadGoals>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__LOAD_GOALS),
        switchMap((act: PayloadAction<ActionType, LoadGoals>) => {
            const service =
                deps.injector.get<CommitmentService>(CommitmentService);
            const behaviourId = act.payload.focus
                ? act.payload.focus.behaviourId
                : 0;
            const subBehaviourId = act.payload.focus
                ? act.payload.focus.subBehaviourId
                : 0;
            const observationIds = act.payload.linkedObservations
                ? act.payload.linkedObservations.map((s) =>
                      s.event.eventId.toString(),
                  )
                : [];

            const obses: [Observable<FocusKPI[]>, Observable<CommitmentKpi[]>] =
                [
                    service.getFocusGoalKpis(
                        behaviourId,
                        subBehaviourId,
                        act.payload.performerId,
                        observationIds,
                    ),
                    service.getOtherCommitmentsGoalsAndClosed(
                        act.payload.commitmentId,
                        act.payload.performerId,
                    ),
                ];

            return forkJoin(obses).pipe(
                map((ret) =>
                    loadGoalsResult(
                        {
                            ...act.payload,
                            focusKpis: ret[0],
                            otherCommitmentKpis: ret[1],
                        },
                        act.storePath,
                    ),
                ),
                catchError((err) => {
                    console.log(err);
                    return of(
                        loadGoalsResult({ ...act.payload }, act.storePath),
                    );
                }),
            );
        }),
    );

export default [
    goalCommitmentInit,
    goalCommitmentRetrieveData,
    goalCommitmentRetrieveGoalHistory,
    goalCommitmentRetrieveGoalEventHistory,
    loadGoals,
];
